import { PermissionConstants } from '../constants/permission-constants.constant';
import { SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CommonDataService } from '../services/common-data.service';
import { environment } from 'src/environments/environment';

export function isNumeric(value: any): boolean {
  return !isNaN(Number(value));
}

export function isNullOrEmpty(value: any): boolean {
  return typeof value === 'undefined' || value === null || (typeof value === 'string' && value === '');
}

export function isDate(input: any) {
  return Object.prototype.toString.call(input) === '[object Date]';
}

export function isEmpty(value: any) {
  return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0) || (!isDate(value) && typeof value === 'object' && Object.keys(value).length === 0);
}

export function isNotEmpty(value: any) {
  return !isEmpty(value);
}

export function deepCopy<T>(obj: T): T | null {

  const seen = new WeakMap(); // 防止循環引用

  function clone(value: any): any {
    if (value == null || typeof value !== 'object') {
      return value; // 基本型態直接回傳
    }

    if (seen.has(value)) {
      return seen.get(value); // 處理循環引用
    }

    if (value instanceof Date) {
      return new Date(value.getTime());
    }

    if (value instanceof RegExp) {
      return new RegExp(value);
    }

    if (value instanceof Map) {
      const copiedMap = new Map();
      seen.set(value, copiedMap);
      value.forEach((val, key) => copiedMap.set(key, clone(val)));
      return copiedMap;
    }

    if (value instanceof Set) {
      const copiedSet = new Set();
      seen.set(value, copiedSet);
      value.forEach((val) => copiedSet.add(clone(val)));
      return copiedSet;
    }

    if (Array.isArray(value)) {
      const copiedArray: any[] = [];
      seen.set(value, copiedArray);
      value.forEach((item) => copiedArray.push(clone(item)));
      return copiedArray;
    }

    if (typeof value === 'object') {
      const copiedObject: Record<string, any> = {};
      seen.set(value, copiedObject);
      Object.keys(value).forEach((key) => {
        copiedObject[key] = clone(value[key]);
      });
      return copiedObject;
    }

    return value;
  }

  return clone(obj);
}


export function isJSON(value: string): boolean {
  try {
    JSON.parse(value);
  } catch {
    return false;
  }
  return true;
}

export function isObject(item: any): boolean {
  return (item && typeof item === 'object' && !Array.isArray(item));
}

export function mergeDeep(target: any, source: any): any {
  const output = Object.assign({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key: any) => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = mergeDeep(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
}

export function getMaxDate(): Date {
  return new Date(8640000000000000);
}

export function getDataScopeByPermission(userProfile: any, permissionCode: PermissionConstants): any{
  const filterRoles = getRolesByPermission(userProfile, permissionCode);

  return Object.values(filterRoles).flatMap((role: any) => role.scopes).map((x: any) => {
    return {
        tenant: x.tenantCode === '' ? null : x.tenantCode,
        dept: x.dept === '' ? null : x.dept,
        customer: x.customer === '' ? null : x.customer,
        facility: x.facilityCode === '' ? null : x.facilityCode,
        orgId: x.orgId === '' ? null : x.orgId,
        brand: x.brand === '' ? null : x.brand,
        forwarder: x.forwarderCode === '' ? null : x.forwarderCode
    };
  });
}

export function getRoleScopeColumnMappings(userProfile: any, permissionCode: PermissionConstants): any {
  const filterRoles = getRolesByPermission(userProfile, permissionCode);

  const roleScopeColumnMappings = Object.values(filterRoles).flatMap((role: any) => role.roleScopeColumnMappings).filter((x: any) => x).map((x: any) => {
    return {
        scopeColumnName: x.scopeColumnName === '' ? null : x.scopeColumnName,
        functionName: x.functionName === '' ? null : x.functionName,
        mappingColumnName: x.mappingColumnName === '' ? null : x.mappingColumnName
    };
  });

  const set = new Set();
  return roleScopeColumnMappings.filter((item: any) => !set.has(item) ? set.add(item) : false).filter((x: any) => x.functionName === permissionCode);
}

export function getTenantByPermission(userProfile: any, permissionCode: PermissionConstants): any {
  const filterRoles = getRolesByPermission(userProfile, permissionCode);

  return Object.values(filterRoles).flatMap((role: any) => role.scopes).map((x: any) => {
    return x.tenantCode;
  });
}

export function getDeptByPermission(userProfile: any, permissionCode: PermissionConstants): any{
  const filterRoles = getRolesByPermission(userProfile, permissionCode);

  return Object.values(filterRoles).flatMap((role: any) => role.scopes).map((x: any) => {
    return x.dept;
  });
}

export function getFacilityByPermission(userProfile: any, permissionCode?: PermissionConstants): (string | null)[] {
  const filterRoles = getRolesByPermission(userProfile, permissionCode);
  return Object.values(filterRoles).flatMap((role: any) => role.scopes).map((x: any) => {
    return x.facilityCode;
  });
}

export function getOrgIdByPermission(userProfile: any, permissionCode: PermissionConstants): any {
  const filterRoles = getRolesByPermission(userProfile, permissionCode);
  return Object.values(filterRoles).flatMap((role: any) => role.scopes).map((x: any) => {
    return x.orgId;
  });
}

export function getBrandByPermission(userProfile: any, permissionCode: PermissionConstants): any{
  const filterRoles = getRolesByPermission(userProfile, permissionCode);

  return Object.values(filterRoles).flatMap((role: any) => role.scopes).map((x: any) => {
    return x.brand;
  });
}

export function getForwarderByPermission(userProfile: any, permissionCode: PermissionConstants): any{
  const filterRoles = getRolesByPermission(userProfile, permissionCode);

  return Object.values(filterRoles).flatMap((role: any) => role.scopes).map((x: any) => {
    return x.forwarderCode;
  });
}

export function getCustomerByPermission(userProfile: any, permissionCode: PermissionConstants): any{
  const filterRoles = getRolesByPermission(userProfile, permissionCode);

  return Object.values(filterRoles).flatMap((role: any) => role.scopes).map((x: any) => {
    return x.customer;
  });
}

function getRolesByPermission(userProfile: any, permissionCode?: PermissionConstants): any{
  if (!userProfile || !userProfile.roles) {
    return [];
  }

  return deepCopy(userProfile.roles.filter((role: any) => {
    const permissionCodes = role.permissions.map((permission: any) => {
      return permission.permissionCode;
    });
    if (permissionCode) {
      return permissionCodes.includes(permissionCode);
    }
    return permissionCodes;
  }));
}

export function getAllPermission(userProfile: any): any{
  const roles = Object.values(userProfile.roles).flatMap((role: any) => role.permissions);
  return deepCopy(roles)?.map((x: any) => {
    return x.permissionCode;
  });
}

export function getDefaultDT(): Date[] {
  const d = new Date();
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2) {
    month = '0' + month;
  }

  if (day.length < 2){
    day = '0' + day;
  }
  const temp = [];
  temp[0] = new Date([year, month, day].join('-') + ' ' + '00:00:00');
  temp[1] = new Date([year, month, day].join('-') + ' ' + '23:59:59');
  return temp;
}

export function getCreateDateBy(addDays: number = 0): Date[] {
  const d = new Date();
  d.setDate(d.getDate() + addDays);
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2){
    month = '0' + month;
  }

  if (day.length < 2){
    day = '0' + day;
  }
  const temp = [];
  temp[0] = new Date([year, month, day].join('-') + ' ' + '00:00:00');
  temp[1] = new Date([year, month, day].join('-') + ' ' + '23:59:59');
  return temp;
}

export function addDays(date: Date, days: number): Date {
  date.setDate(date.getDate() + days);
  return date;
}

export function addMonth(date: Date, month: number): Date {
  date.setMonth((date.getMonth() + month));
  return date;
}

export function laaSFormatString(str: string, strArray: string[]): string {
  for (let i = 0; i <= strArray.length; i++) {
       const reg = new RegExp('\\{' + i + '\\}', 'gm');
       str = str.replace(reg, strArray[i]);
  }
  return str;
}

export function onChanges(changes: SimpleChanges, selectedCols: any): any {
  if (!isNullOrEmpty(changes.defaultColumns)) {
    changes.defaultColumns.currentValue?.forEach((x: any) => {
      if (isNullOrEmpty(x.sort)) {
        x.sort = false;
      }
      if (isNullOrEmpty(x.display)) {
        x.display = true;
      }
    });

    if (!isNullOrEmpty(changes.defaultColumns.currentValue)) {
      const screenWidth = window.screen.width;
      const displayColumn = changes.defaultColumns.currentValue.filter(
        (x: any) => x.display
      );
      const totalColumnDisplay = displayColumn.length;
      const width = screenWidth / totalColumnDisplay;
      const columnMinWidth = width < 100 ? 100 : width;
      changes.defaultColumns.currentValue.forEach((x: any) => {
        if (isNullOrEmpty(x.width)) {
          x.width = columnMinWidth;
        }
      });
      selectedCols = displayColumn;
    }
  }
}

export function columnReordered(columns: any, defaultColumns: any[]): void {
  const newColumnOrderList: string[] = [];
  columns.forEach((col: any) => {
    for (const defaultCol of defaultColumns) {
      if (defaultCol.field === col.field) {
        newColumnOrderList.push(col.field);
        break;
      }
      const newIndex = columns.findIndex(
        (y: { field: any }) => y.field === defaultCol.field
      );
      if (newIndex < 0 && !newColumnOrderList.includes(defaultCol.field)) {
        newColumnOrderList.push(defaultCol.field);
      }
    }
  });

  defaultColumns.forEach((col) => {
    if (!newColumnOrderList.includes(col.field)) {
      newColumnOrderList.push(col.field);
    }
  });

  newColumnOrderList.forEach((field, index) => {
    const idx = defaultColumns.findIndex((y) => y.field === field);
    if (idx > -1) {
      defaultColumns[idx].order = index;
    }
  });
}

export function nullIfEmptyArray<T>(list: T[] | null): T[] | null {
  return (list && list.length > 0) ? list : null; // if list is null, then return null
}

export function genTableTranslation(translateService: TranslateService, fields: string[], dates: string[], fieldSorts: string[], jsons: string[]): any[] {
  const tmpcolumn: any[] = [];
  for (let i = 0; i < fields.length; i++) {
    if (dates.includes(fields[i])) {
      tmpcolumn[i] = ({ field: fields[i], header: jsons[i], dataType: 'date', sort: fieldSorts.includes(fields[i]) });
      continue;
    }
    tmpcolumn[i] = ({ field: fields[i], header: jsons[i], sort: fieldSorts.includes(fields[i]) });
  }
  return tmpcolumn;
}

export function isDeepLinkAvailable(htmlString: string): boolean {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const links = doc.body.querySelectorAll('a#deeplink');
  return links.length > 0;
}

export function extractLinkPath(htmlString: string): string | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const link = doc.querySelector('a#deeplink');
  if (link) {
    return link.getAttribute('href');
  }
  return null;
}

export function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}


export function mapToObject<T>(map: Map<string, T> | undefined): { [p: string]: T | undefined | null } | null {
  if (!map || map.size === 0) {
    return null;
  }

  return Array.from(map).reduce((obj, [key, value]) => {
    obj[key] = value;
    return obj;
  }, {} as { [key: string]: T | undefined });
}

export function concatHeaderAndDetailTaskArray(headerTaskCode: string[] | undefined, detailTaskCode: string[] | undefined | null): string[] {
  const taskCode = headerTaskCode;
  const taskCodeArray1 = taskCode ?? [];
  const alldetailTaskCode = detailTaskCode;
  const taskCodeArray2 = alldetailTaskCode && alldetailTaskCode.length ? [...alldetailTaskCode] : [];
  const allSelectTaskCode = taskCodeArray1.concat(taskCodeArray2);

  return allSelectTaskCode;
}
export function genOptionsTranslation(
    translateService: TranslateService,
    options: any[]
  ): any {
    options?.forEach((element) => {
      if( !isNullOrEmpty(element?.header) ) {
        translateService.get(element?.header)?.subscribe((translation: string) => {
          element.header = translation;
        });
      }
    });
    return options;
}

export function getDateFormatted(date: Date): string {
  return date.toISOString().split('T')[0];
}

export function trimString(str: string): string {
  return str.trim();
}

export function initLaasPortalVersion(commonDataService: CommonDataService, userType: string): void {
  commonDataService.getSystemParam('LaasPortalVersionBy' + userType).subscribe({
    next: (result: any) => {
      const version = result.length > 0 && result[0].version ? `/v${result[0].version}` : '/';
      const baseElement = document.querySelector('base');
      if (!baseElement) {
        return;
      }
      const href = baseElement.getAttribute('href');
      if (href !== version) {
        window.location.href = `${environment.laasPortalUrl}${version}`;
      }
    }
  });
}

// This method handles the keydown event for the dropdown.
// It prevents the default behavior and stops propagation
// to ensure that the arrow keys only affect the p-dropdown.
export function onDropdownKeydown(event: KeyboardEvent): void {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.stopPropagation(); // Avoid focus jumping out
    event.preventDefault();  // Ensure arrow keys only affect p-dropdown
  }
}

export function readBlobAsJson<T>(blob: Blob): Promise<T> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        if(isJSON(reader.result as string)) {
          const json = JSON.parse(reader.result as string) as T;
          resolve(json);
        } else {
          resolve(reader.result as T);
        }
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = (err) => {
      reject(err);
    };
    reader.readAsText(blob);
  });
}

export function toUTCDate(localDate: Date): Date | undefined {
  if( !localDate || !(localDate instanceof Date)) {
    return undefined;
  }
  return new Date(
    localDate.getUTCFullYear(),
    localDate.getUTCMonth(),
    localDate.getUTCDate(),
    localDate.getUTCHours(),
    localDate.getUTCMinutes(),
    localDate.getUTCSeconds()
  );
}
